import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import {
  Box, Text, Badge, Group, Button, Tabs, Center, Loader,
  Table, ActionIcon, Tooltip, Stack, Modal, Textarea, Alert,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconCheck, IconX, IconArrowsExchange, IconArrowsTransferDown, IconAlertCircle,
} from '@tabler/icons-react'
import {
  getBorrows, approveBorrow, rejectBorrow,
  getTransfers, approveTransfer, rejectTransfer,
} from '../api/client'
import SmartDateInput from '../components/SmartDateInput'

const TH_STYLE = { fontSize: 11, color: '#94a3b8', fontWeight: 600 as const, textTransform: 'uppercase' as const, letterSpacing: '0.3px' }

export default function ApprovalsPage() {
  const { t } = useTranslation()
  const { isMobile, isSmall } = useBreakpoint()

  const [borrows,   setBorrows]   = useState<any[]>([])
  const [transfers, setTransfers] = useState<any[]>([])
  const [loading,   setLoading]   = useState(true)

  // Borrow approve modal
  const [approvingBorrow, setApprovingBorrow] = useState<any>(null)
  const [dueDate, setDueDate] = useState('')
  const [borrowModalOpen, { open: openBorrowModal, close: closeBorrowModal }] = useDisclosure(false)

  // Transfer reject modal
  const [rejectingTransfer, setRejectingTransfer] = useState<any>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [transferRejectOpen, { open: openTransferReject, close: closeTransferReject }] = useDisclosure(false)

  const load = async () => {
    setLoading(true)
    try {
      const [b, tr] = await Promise.all([getBorrows(), getTransfers()])
      setBorrows(b.data ?? [])
      setTransfers(tr.data ?? [])
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const pendingBorrows   = borrows.filter(b => b.status === 'PENDING')
  const pendingTransfers = transfers.filter(t => t.status === 'PENDING')
  const totalPending = pendingBorrows.length + pendingTransfers.length

  // ── Borrow ────────────────────────────────────────────────────────────────
  const handleOpenApproveBorrow = (borrow: any) => {
    setApprovingBorrow(borrow); setDueDate(''); openBorrowModal()
  }

  const handleApproveBorrow = async () => {
    if (!approvingBorrow) return
    try {
      await approveBorrow(approvingBorrow.id, { dueDate: dueDate || undefined })
      notifications.show({ color: 'teal', message: 'Borrow approved' })
      closeBorrowModal(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t("common.error","Error") })
    }
  }

  const handleRejectBorrow = (borrow: any) => {
    modals.openConfirmModal({
      title: t('borrow.rejectConfirm', 'Reject Borrow Request'),
      children: <Text size="sm">{t('approvals.rejectBorrowFrom', 'Reject borrow from {{name}}?', { name: borrow.borrowerName })}</Text>,
      labels: { confirm: t('common.reject','Reject'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await rejectBorrow(borrow.id)
          notifications.show({ color: 'orange', message: t("approvals.borrowRejected","Borrow rejected") })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t("common.error","Error") })
        }
      },
    })
  }

  // ── Transfer ──────────────────────────────────────────────────────────────
  const handleApproveTransfer = (tr: any) => {
    modals.openConfirmModal({
      title: t('transfer.approve', 'Approve Transfer Request'),
      children: (
        <Stack gap="xs">
          <Text size="sm">{t('approvals.approveTransferOf', 'Approve transfer of {{count}} item(s)?', { count: (tr.items ?? []).length })}</Text>
          <Text size="xs" c="dimmed">
            From: <b>{tr.fromDeptCode}</b> → To: <b>{tr.toDeptCode}</b>
          </Text>
          <Text size="xs" c="dimmed">
            Assets: {(tr.items ?? []).map((a: any) => a.code ?? a.name).join(', ')}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('transfer.approve','Approve'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'teal' },
      onConfirm: async () => {
        try {
          await approveTransfer(tr.id)
          notifications.show({ color: 'teal', message: 'Transfer approved — department updated' })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t("common.error","Error") })
        }
      },
    })
  }

  const handleRejectTransfer = (tr: any) => {
    setRejectingTransfer(tr); setRejectReason(''); openTransferReject()
  }

  const handleRejectTransferSubmit = async () => {
    if (!rejectingTransfer) return
    try {
      await rejectTransfer(rejectingTransfer.id, { reason: rejectReason || undefined })
      notifications.show({ color: 'orange', message: 'Transfer rejected' })
      closeTransferReject(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t("common.error","Error") })
    }
  }

  return (
    <Box p={isMobile ? 16 : 24}>
      {/* Header */}
      <Group justify="space-between" mb={isMobile ? 'md' : 'xl'}>
        <Box>
          <Group gap={8} align="center">
            <Text style={{ fontSize: isSmall ? 20 : 24, fontWeight: 600, fontFamily: 'Inter, sans-serif', color: '#0f172a' }}>
              {t("approvals.title","Approvals")}
            </Text>
            {totalPending > 0 && <Badge color="red" size="lg" circle>{totalPending}</Badge>}
          </Group>
          {!isMobile && <Text size="sm" c="dimmed">{t("approvals.subtitle")}</Text>}
        </Box>
        <Button variant="light" onClick={load} size={isMobile ? 'sm' : 'md'}>{t("common.refresh", "Refresh")}</Button>
      </Group>

      {loading ? <Center p="xl"><Loader color="teal" /></Center> : (
        <Tabs defaultValue="borrows">
          <Tabs.List mb="lg">
            <Tabs.Tab value="borrows" leftSection={<IconArrowsExchange size={14} />}
              rightSection={pendingBorrows.length > 0 ? <Badge size="xs" color="red" circle>{pendingBorrows.length}</Badge> : undefined}>
              {isMobile ? `${t("borrow.title")} (${pendingBorrows.length})` : `${t("approvals.borrowRequests")} (${pendingBorrows.length})`}
            </Tabs.Tab>
            <Tabs.Tab value="transfers" leftSection={<IconArrowsTransferDown size={14} />}
              rightSection={pendingTransfers.length > 0 ? <Badge size="xs" color="red" circle>{pendingTransfers.length}</Badge> : undefined}>
              {isMobile ? `${t("transfer.title")} (${pendingTransfers.length})` : `${t("approvals.transfers")} (${pendingTransfers.length})`}
            </Tabs.Tab>
          </Tabs.List>

          {/* ── Borrow Requests ── */}
          <Tabs.Panel value="borrows">
            {pendingBorrows.length === 0 ? (
              <Center p="xl">
                <Stack align="center" gap="xs">
                  <IconCheck size={40} color="#22c55e" />
                  <Text c="dimmed">{t("approvals.noPending")}</Text>
                </Stack>
              </Center>
            ) : isMobile ? (
              <Stack gap={8}>
                {pendingBorrows.map(b => (
                  <Box key={b.id} p="md" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                    <Group justify="space-between" wrap="nowrap" mb={6}>
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={600} truncate>{b.borrowerName}</Text>
                        <Text size="xs" c="dimmed">{b.borrowerDept ?? '-'}</Text>
                      </Box>
                      <Badge color="yellow" variant="light" size="sm">{t("common.pending","PENDING")}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed" mb={4}>
                      {(b.assets ?? []).length} item(s): {(b.assets ?? []).map((a: any) => a.assetCode).join(', ')}
                    </Text>
                    {b.reason && <Text size="xs" c="dimmed" lineClamp={2} mb={8}>{b.reason}</Text>}
                    <Group gap={8}>
                      <Button size="xs" color="teal" leftSection={<IconCheck size={13} />}
                        onClick={() => handleOpenApproveBorrow(b)} style={{ flex: 1 }}>{t("common.approve","Approve")}</Button>
                      <Button size="xs" color="red" variant="light" leftSection={<IconX size={13} />}
                        onClick={() => handleRejectBorrow(b)} style={{ flex: 1 }}>{t("common.reject","Reject")}</Button>
                    </Group>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
                <Table highlightOnHover>
                  <Table.Thead style={{ background: '#f8fafc' }}>
                    <Table.Tr>
                      {['Borrower','Assets','Requested','Reason','Status',''].map(h => (
                        <Table.Th key={h} style={TH_STYLE}>{h}</Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pendingBorrows.map(b => (
                      <Table.Tr key={b.id}>
                        <Table.Td>
                          <Text size="sm" fw={600}>{b.borrowerName}</Text>
                          <Text size="xs" c="dimmed">{b.borrowerDept ?? '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{(b.assets ?? []).length} item(s)</Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>{(b.assets ?? []).map((a: any) => a.assetCode).join(', ')}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '-'}</Text>
                        </Table.Td>
                        <Table.Td><Text size="xs" c="dimmed" lineClamp={2}>{b.reason ?? '-'}</Text></Table.Td>
                        <Table.Td><Badge color="yellow" variant="light" size="sm">{t("common.pending","PENDING")}</Badge></Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Tooltip label={t("common.approve","Approve")}><ActionIcon color="teal" variant="light" onClick={() => handleOpenApproveBorrow(b)}><IconCheck size={16} /></ActionIcon></Tooltip>
                            <Tooltip label={t("common.reject","Reject")}><ActionIcon color="red" variant="light" onClick={() => handleRejectBorrow(b)}><IconX size={16} /></ActionIcon></Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}
          </Tabs.Panel>

          {/* ── Transfer Requests ── */}
          <Tabs.Panel value="transfers">
            {pendingTransfers.length === 0 ? (
              <Center p="xl">
                <Stack align="center" gap="xs">
                  <IconCheck size={40} color="#22c55e" />
                  <Text c="dimmed">{t("approvals.noPending")}</Text>
                </Stack>
              </Center>
            ) : isMobile ? (
              <Stack gap={8}>
                {pendingTransfers.map(tr => (
                  <Box key={tr.id} p="md" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                    <Group justify="space-between" wrap="nowrap" mb={6}>
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={600} truncate>#{tr.id} — {tr.requestedBy}</Text>
                        <Text size="xs" c="dimmed">{tr.fromDeptCode} → {tr.toDeptCode}</Text>
                      </Box>
                      <Badge color="yellow" variant="light" size="sm">{t("common.pending","PENDING")}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed" mb={8}>
                      {(tr.items ?? []).length} item(s): {(tr.items ?? []).map((a: any) => a.code ?? a.name).join(', ')}
                    </Text>
                    <Group gap={8}>
                      <Button size="xs" color="teal" leftSection={<IconCheck size={13} />}
                        onClick={() => handleApproveTransfer(tr)} style={{ flex: 1 }}>{t("common.approve","Approve")}</Button>
                      <Button size="xs" color="red" variant="light" leftSection={<IconX size={13} />}
                        onClick={() => handleRejectTransfer(tr)} style={{ flex: 1 }}>{t("common.reject","Reject")}</Button>
                    </Group>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
                <Table highlightOnHover>
                  <Table.Thead style={{ background: '#f8fafc' }}>
                    <Table.Tr>
                      {['#','Assets','From','To','Requested By','Requested','Status',''].map(h => (
                        <Table.Th key={h} style={TH_STYLE}>{h}</Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {pendingTransfers.map(tr => (
                      <Table.Tr key={tr.id}>
                        <Table.Td><Text size="sm" fw={700}>#{tr.id}</Text></Table.Td>
                        <Table.Td>
                          <Text size="sm">{(tr.items ?? []).length} item(s)</Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>{(tr.items ?? []).map((a: any) => a.code ?? a.name).join(', ')}</Text>
                        </Table.Td>
                        <Table.Td><Text size="sm" fw={600}>{tr.fromDeptCode}</Text></Table.Td>
                        <Table.Td><Text size="sm" fw={600} c="teal">{tr.toDeptCode}</Text></Table.Td>
                        <Table.Td><Text size="sm">{tr.requestedBy ?? '-'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{tr.createdAt ? new Date(tr.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '-'}</Text></Table.Td>
                        <Table.Td><Badge color="yellow" variant="light" size="sm">{t("common.pending","PENDING")}</Badge></Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Tooltip label={t("approvals.approveTransfer","Approve Transfer")}><ActionIcon color="teal" variant="light" onClick={() => handleApproveTransfer(tr)}><IconCheck size={16} /></ActionIcon></Tooltip>
                            <Tooltip label={t("approvals.rejectTransfer","Reject Transfer")}><ActionIcon color="red" variant="light" onClick={() => handleRejectTransfer(tr)}><IconX size={16} /></ActionIcon></Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      {/* ── Approve Borrow Modal ── */}
      <Modal opened={borrowModalOpen} onClose={closeBorrowModal}
        size={isSmall ? '100%' : 'sm'} radius={isMobile ? 0 : 'lg'} fullScreen={isSmall}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t("borrow.approveBorrowRequest","Approve Borrow Request")}</Text>}>
        <Stack gap="md">
          {approvingBorrow && (
            <Alert icon={<IconAlertCircle size={16} />} color="teal" variant="light">
              <Text size="sm">{t('approvals.approveBorrowOf', 'Approve {{name}} to borrow {{count}} item(s)', { name: approvingBorrow.borrowerName, count: (approvingBorrow.assets ?? []).length })}</Text>
              <Text size="xs" c="dimmed">{(approvingBorrow.assets ?? []).map((a: any) => a.assetCode).join(', ')}</Text>
            </Alert>
          )}
          <SmartDateInput
            label={t("approvals.dueDateLabel","Due Date (return deadline)")}
            placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
            value={dueDate}
            onChange={setDueDate}
            clearable
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeBorrowModal}>{t("common.cancel","Cancel")}</Button>
            <Button onClick={handleApproveBorrow} color="teal" leftSection={<IconCheck size={16} />}>{t("common.approve","Approve")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Reject Transfer Modal ── */}
      <Modal opened={transferRejectOpen} onClose={closeTransferReject} size="sm" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{t("approvals.rejectTransferRequest","Reject Transfer Request")}</Text>}>
        <Stack gap="md">
          {rejectingTransfer && (
            <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
              <Text size="sm">Reject transfer #{rejectingTransfer.id}: <b>{rejectingTransfer.fromDeptCode}</b> → <b>{rejectingTransfer.toDeptCode}</b></Text>
            </Alert>
          )}
          <Textarea label={t("approvals.reasonOptional","Reason (optional)")} placeholder={t("approvals.reasonForRejectionPlaceholder","Reason for rejection...")}
            value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeTransferReject}>{t("common.cancel","Cancel")}</Button>
            <Button onClick={handleRejectTransferSubmit} color="red" leftSection={<IconX size={16} />}>{t("common.reject","Reject")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
